import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Receipt, Info, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function VATSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    vat_enabled: false,
    vat_rate: 20,
    vat_number: "",
    vat_label: "VAT",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await API.get("/settings/vat");
      setSettings({
        vat_enabled: res.data.vat_enabled || false,
        vat_rate: res.data.vat_rate || 20,
        vat_number: res.data.vat_number || "",
        vat_label: res.data.vat_label || "VAT",
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load VAT settings");
    }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put("/settings/vat", settings);
      toast.success("VAT settings saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="w-7 h-7" weight="duotone" />
            VAT Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure Value Added Tax (VAT) settings for your quotes and invoices
          </p>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" weight="duotone" />
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <p className="font-medium mb-1">About VAT Settings</p>
                <p>
                  When VAT is enabled, all quotes and invoices will automatically include VAT calculations
                  based on the rate you specify. The VAT amount will be displayed separately in PDFs and totals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle>VAT Configuration</CardTitle>
            <CardDescription>
              These settings apply to all quotes and invoices for your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              {/* VAT Enabled Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">Enable VAT</Label>
                  <p className="text-sm text-muted-foreground">
                    Apply VAT to all quotes and invoices
                  </p>
                </div>
                <Switch
                  checked={settings.vat_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, vat_enabled: checked })}
                />
              </div>

              {/* VAT Rate */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rate">VAT Rate (%)</Label>
                  <Input
                    id="rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.vat_rate}
                    onChange={(e) => setSettings({ ...settings, vat_rate: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g., 20"
                    disabled={!settings.vat_enabled}
                    className={!settings.vat_enabled ? "opacity-50" : ""}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Standard UK VAT rate is 20%
                  </p>
                </div>

                <div>
                  <Label htmlFor="label">VAT Label</Label>
                  <Input
                    id="label"
                    type="text"
                    value={settings.vat_label}
                    onChange={(e) => setSettings({ ...settings, vat_label: e.target.value })}
                    placeholder="e.g., VAT, GST, Sales Tax"
                    disabled={!settings.vat_enabled}
                    className={!settings.vat_enabled ? "opacity-50" : ""}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Display name for tax (e.g., VAT, GST)
                  </p>
                </div>
              </div>

              {/* Registration Number */}
              <div>
                <Label htmlFor="registration_number">VAT Registration Number</Label>
                <Input
                  id="registration_number"
                  type="text"
                  value={settings.vat_number}
                  onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                  placeholder="e.g., GB123456789"
                  disabled={!settings.vat_enabled}
                  className={!settings.vat_enabled ? "opacity-50" : ""}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Your official VAT registration number (displayed on quotes and invoices)
                </p>
              </div>

              {/* Preview */}
              {settings.vat_enabled && (
                <div className="border rounded-lg p-4 bg-muted/20">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" weight="fill" />
                    Preview
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Example Subtotal:</span>
                      <span className="font-semibold">£1,000.00</span>
                    </div>
                    <div className="flex justify-between text-blue-600 dark:text-blue-400">
                      <span>{settings.vat_label} ({settings.vat_rate}%):</span>
                      <span className="font-semibold">£{(1000 * settings.vat_rate / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t text-base font-bold">
                      <span>Total with {settings.vat_label}:</span>
                      <span className="text-primary">£{(1000 * (1 + settings.vat_rate / 100)).toFixed(2)}</span>
                    </div>
                    {settings.vat_number && (
                      <p className="text-xs text-muted-foreground pt-2 border-t">
                        {settings.vat_label} Reg No: {settings.vat_number}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchSettings()}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" weight="fill" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Important Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>• VAT settings apply to all new quotes created after enabling</p>
            <p>• Existing quotes will retain their original VAT settings</p>
            <p>• VAT is calculated on the total after markups are applied</p>
            <p>• VAT registration number is displayed on all PDF documents</p>
            <p>• Disabling VAT will not affect existing quotes, only new ones</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
